import classNames from "classnames";
import isNull from "lodash/isNull";
import moment from "moment";
import { DEFAULT_DAYS, DEFAULT_MINUTES } from "PFComponents/availability/utils";
import { Select } from "PFComponents/select";
import { roundToDecimals } from "PFCore/helpers/rounding";
import useAvailabilityLimits from "PFCore/hooks/use_availability_limits";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { convertToPercentage } from "PFCore/utilities/math";
import { AccountAvailability } from "PFTypes";
import { AvailabilityDuration, AvailabilityMode, DurationMode, StandardRange } from "PFTypes/booking";
import { Option } from "PFTypes/options";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { AvailabilityRangeCalendar } from "../../availability_range_calendar";
import {
  DEFAULT_MONTHS_FORWARD_LIMIT,
  DURATION_MODE_ERROR_KEY,
  DURATION_VALUE_ERROR_KEY
} from "../../constants";
import { getFieldError } from "../../errors_helper";
import { LoadEstimationData } from "../../hooks/use_load_estimation";
import { InfoIcon } from "../info_icon/info_icon";
import { NumberInput } from "../number_input";
import { SideInfo } from "../parts/side_info";
import commonCss from "../rules_carousel.module.scss";
import css from "./standard_rule.module.scss";
import { useDurationModeOptions } from "./use_duration_mode_options";
import { useModeOptions } from "./use_mode_options";

const DEFAULT_EMPTY_VALUE = 0;

const sanitizeDurationValue = (
  value: number | string,
  mode: AvailabilityDuration["mode"],
  toHours?: boolean
) => {
  const durationValueInt = Number(value);

  if (isNaN(durationValueInt) || value === "" || isNull(value)) {
    return DEFAULT_EMPTY_VALUE;
  }

  if (mode === DurationMode.Hours) {
    return toHours ? roundToDecimals(durationValueInt / 60) : Math.round(durationValueInt * 60);
  }

  return durationValueInt;
};

type StandardRuleProps = {
  accountAvailability: AccountAvailability;
  errors?: Record<string, string>;
  mode: Omit<AvailabilityMode, AvailabilityMode.TimeRule>;
  allowedModes: AvailabilityMode[];
  allowedDurationModes: DurationMode[];
  qaId?: string;
  range: StandardRange;
  minDate?: { start: string; end?: string };
  onChange: (value: any) => void;
  onModeChange?: (mode: Omit<AvailabilityMode, AvailabilityMode.TimeRule>) => void;
  portalRef?: React.RefObject<HTMLDivElement | null>;
  loadData: LoadEstimationData;
  isDisabled?: boolean;
};

export const StandardRule = ({
  accountAvailability,
  errors = {},
  mode,
  qaId,
  allowedModes,
  allowedDurationModes,
  range: { start, end, duration },
  minDate,
  onChange,
  onModeChange,
  portalRef,
  loadData,
  isDisabled
}: StandardRuleProps) => {
  const { t } = useTranslation("translation", { keyPrefix: "availabilityRequirement" });
  const { formatISODate } = useDateFormatter();
  const { maxDate } = useAvailabilityLimits();

  const { availabilityModes } = useModeOptions(mode, allowedModes);
  const { durationModeOptions } = useDurationModeOptions(allowedDurationModes);

  const canEdit = start && end && !isDisabled;
  const matchMonthsForwardLimit = accountAvailability?.match_months_limit || DEFAULT_MONTHS_FORWARD_LIMIT;

  const durationValue = useMemo(() => sanitizeDurationValue(duration.value, duration.mode, true), [duration]);
  const matchMaxDate = useMemo(
    () => formatISODate(moment().add(matchMonthsForwardLimit, "months").endOf("month")),
    [formatISODate, matchMonthsForwardLimit]
  );

  const handleDatesChange = (start: string, end: string) => onChange({ start, end });

  const handleDurationChange = (value: string) => {
    onChange({ duration: { ...duration, value: sanitizeDurationValue(value, duration.mode) } });
  };

  const handleDurationModeChange = ({ original: mode }: Option<DurationMode>) => {
    onChange({
      duration: { ...duration, mode, value: mode === DurationMode.Hours ? DEFAULT_MINUTES : DEFAULT_DAYS }
    });
  };

  const handleModeChange = ({ original: mode }: Option<AvailabilityMode>) => onModeChange?.(mode);

  const { totalAvailableHours, percentageLoad } = loadData;

  return (
    <div className={css.calendars} data-qa-id={qaId}>
      <span className={commonCss.row}>
        <AvailabilityRangeCalendar
          key={`${start}+${end}+${mode}`}
          // @ts-ignore
          start={start}
          end={end || undefined}
          mode={mode as AvailabilityMode}
          maxDate={maxDate}
          matchMaxDate={matchMaxDate}
          onChange={handleDatesChange}
          errors={errors}
          minDate={minDate}
          portalRef={portalRef}
        />
        <SideInfo
          label={t("totalAvailable")}
          value={totalAvailableHours !== null ? t("nHours", { count: Math.round(totalAvailableHours) }) : "-"}
          tooltipContent={t("tooltips.totalAvailable")}
        />
      </span>

      <span className={commonCss.row}>
        <div className={css.durationWrap}>
          <NumberInput
            placeholder={t("setAmount")}
            label={t("timeRequired")}
            className={css.numberInput}
            onChange={(value) => handleDurationChange(value ? String(value) : "")}
            error={getFieldError(errors, DURATION_VALUE_ERROR_KEY)}
            disabled={!canEdit}
            controlledValue={durationValue ?? undefined}
          />
          <Select<DurationMode>
            title={t("durationMode")}
            value={duration.mode}
            className={classNames(css.durationModeSelect, css.noLabelField)}
            options={durationModeOptions}
            onChange={handleDurationModeChange}
            disabled={!canEdit}
            error={getFieldError(errors, DURATION_MODE_ERROR_KEY)}
          />
          <Select<AvailabilityMode>
            title={t("availabilityMode")}
            value={mode as AvailabilityMode}
            className={classNames(css.availabilityModeSelect, css.noLabelField)}
            options={availabilityModes}
            onChange={handleModeChange}
            disabled={!canEdit}
            locked={!onModeChange}
            lockedTip={t("modeLockedTip")}
          />
          <InfoIcon />
        </div>
        <SideInfo
          label={t("requiredPercent")}
          value={convertToPercentage(percentageLoad, { isNormalized: true })}
          tooltipContent={t("tooltips.requiredPercent")}
        />
      </span>
    </div>
  );
};
